import { Model } from "../../shared/models/model";

import { useVisitTypeStore } from "../store/visit-type";

export class Node extends Model {
  get defaults() {
    return {
      id: undefined,
    };
  }

  get isRootStart() {
    return this.isStart && !this.isNested;
  }

  get isStart() {
    return this.name === "StartNode";
  }

  get isBroadcast() {
    return this.name === "BroadcastNode";
  }

  get isSelectVisitType() {
    return this.name === "SelectVisitTypeNode";
  }

  get isChangeVisitType() {
    return this.name === "ChangeVisitTypeNode";
  }

  get isSwitchTree() {
    return this.name === "SwitchTreeNode";
  }

  // These nodes can clear any existing decision tree nesting
  get canForceSwitchTree() {
    return (this.isSelectVisitType || this.isChangeVisitType) && this.switchDecisionTree;
  }

  get isAllowBooking() {
    return this.name === "AllowBookingNode";
  }

  get isMissingVisitType() {
    return this.name === "MissingVisitTypeNode";
  }

  get isContinue() {
    return this.name === "ContinueNode";
  }

  get isNonStepDeny() {
    return this.isDeny && !this.step;
  }

  get isDeny() {
    return this.name === "DenyNode";
  }

  get context() {
    return {};
  }

  /**
   * Returns the user-provided state for this node.
   *
   * @returns Object
   *
   */
  get currentState() {
    return {};
  }

  /**
   * Sets the user-provided state for this node.
   *
   * @param {Object} state - The new state to set
   */
  setCurrentState(state) {
    Object.assign(this, state);
  }

  /**
   * Determines whether the node should switch to a different decision tree
   *
   * @param {String} visitTypeId - The ID of the visit type that was selected
   * @param {Object|undefined} context - The global context of the decision tree
   * @returns String - The ID of the next node to render
   *
   */
  determineDecisionTreeSwitch(visitTypeId, context) {
    const nodeId = this.targetNodeId;

    // If the node is not configured to switch decision trees, we should not switch
    if (!this.switchDecisionTree) {
      return { isSwitch: false, nodeId };
    }

    // If we don't have a visit type, we should not switch
    if (!visitTypeId) {
      return { isSwitch: false, nodeId };
    }

    // If we don't have a new visit type, we should not switch
    if (visitTypeId === context.visitTypeId) {
      return { isSwitch: false, nodeId };
    }

    const { visitTypes } = useVisitTypeStore();
    const visitType = visitTypes.find((v) => v.id === visitTypeId);

    const decisionTreeId = visitType?.decisionTreeId;

    if (!decisionTreeId) {
      // If the newly selected visit type does not have a decision tree, we should end the decision tree
      return { isSwitch: true, nodeId: "continue" };
    } else if (decisionTreeId !== context.decisionTreeId) {
      // If the newly selected visit type has a different decision tree than the current one, we should switch to it
      return { isSwitch: true, nodeId: `decision-tree-${decisionTreeId}-node-start` };
    }

    return { isSwitch: false, nodeId };
  }

  /**
   * Returns all possible next node IDs.
   *
   * @returns Array
   */
  get possibleNextNodeIds() {
    return [];
  }

  /**
   * Perform any actions that should occur before the node is rendered.
   *
   * @param {Object|undefined} context (optional) - The global context of the decision tree
   */
  beforeShow() {
    return undefined;
  }

  /**
   * Determines whether the decision tree should automatically advance to the next node
   * after this node is rendered.
   *
   * @param {Object|undefined} context (optional) - The global context of the decision tree
   * @returns Boolean
   */
  autoAdvance() {
    return false;
  }

  /**
   * Determines the next node to render after this node.
   *
   * @param {Object|undefined} context (optional) - The global context of the decision tree
   * @returns String|undefined - The ID of the next node to render
   */
  nextNodeId() {
    return undefined;
  }

  /**
   * Resets the node to its initial state.
   */
  reset() {
    return undefined;
  }
}
